import { BuilderContent } from "@builder.io/sdk";
import Cookies from "cookies";
import { useRouter } from "next/router";
import qs from "querystring";
import React, { useEffect } from "react";
import { v4 as uuidV4 } from "uuid";

import { DEFAULT_PER_PAGE_VALUE, SEARCH_PAGE_COR_ID_COOKIE_KEY } from "@/shared/constants";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import EVENTS from "@/analytics/events";
import { trackPageLoad } from "@/analytics/tracking";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import { getBuilderMessages, getBuilderNavLinks } from "@/pages/api/builder/builder";
import { fetchStoresSearch } from "@/pages/api/store-directory/categories";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";

import StoreDirectorySearch from "@/components/store-directory/store-directory-search";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";
import { StoresWithCountProps } from "@/interfaces/stores-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type SearchProps = {
    searchVersion: string;
    searchTerm: string;
    httpCorrelationId?: string;
};

type OwnProps = {
    storesSearchResults: StoresWithCountProps;
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & LocaleProps & SearchProps;
type Params = {
    search_version: string;
    q?: string;
};

init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({
    query,
    locale,
    resolvedUrl,
    req,
    res,
}) => {
    const cookies = new Cookies(req, res, { secure: false });
    let { search_version: searchVersion, q: searchTerm } = query;

    if (!searchVersion || !searchTerm) {
        res.statusCode = 400;
        res.statusMessage = "Search version and search term are required";

        searchVersion = "3";
        searchTerm = "";
    }

    let httpCorrelationId = cookies.get(SEARCH_PAGE_COR_ID_COOKIE_KEY);
    if (!httpCorrelationId) {
        httpCorrelationId = uuidV4();
        cookies.set(SEARCH_PAGE_COR_ID_COOKIE_KEY, httpCorrelationId);
    }

    const searchQuery = qs.stringify({
        has_in_store: false,
        offset: 0,
        per_page: DEFAULT_PER_PAGE_VALUE,
        search_version: searchVersion,
        q: searchTerm,
        cor_id: httpCorrelationId,
        locale: locale,
    });

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        storesSearchResults: () => fetchStoresSearch(locale, searchQuery),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        props: {
            locale,
            searchVersion,
            searchTerm,
            httpCorrelationId,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const Search: React.FC<Props> = ({
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    locale,
    searchVersion,
    searchTerm,
    httpCorrelationId,
    storesSearchResults,
    builderMessages,
    builderNavLinks,
}) => {
    const router = useRouter();

    useEffect(() => {
        trackPageLoad(router.asPath, locale, EVENTS.SHOP_DIRECTORY_SEARCH_RESULT_VIEW, {
            query: searchTerm,
            resultsCount: storesSearchResults.totalCount,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <StoreDirectorySearch
                searchVersion={searchVersion}
                searchTerm={searchTerm}
                storesSearchResults={storesSearchResults}
                httpCorrelationId={httpCorrelationId}
            />
        </DefaultLayout>
    );
};

export default Search;
